import {
  addScrollTrap,
  focusFirstDescendant,
  focusLastDescendant
} from "../Utilities/node";

declare global {
  interface Window {
    ani_modalsInitialized?: boolean;
    ani_modals?: Map<string, Modal>;
  }
}

export type Modal = {
  close: (e?: Event) => void;
  element: HTMLElement;
  isOpen: () => boolean;
  open: () => void;
};

export type CloseModalEvent = CustomEvent<{
  /** The original event, if any, that toggled the collapsible state. */
  originalEvent?: Event;
}>;

if (!window.ani_modals) {
  window.ani_modals = new Map<string, Modal>();
}

const modals = window.ani_modals;

const initializeDocumentEventListeners = () => {
  document.addEventListener("keydown", e => {
    if (e.key === "Escape") {
      modals.forEach(modalRecord => {
        if (modalRecord.element.dataset.closeable === "true") {
          modalRecord.close(e);
        }
      });
    }
  });

  // Add focus trap
  document.addEventListener(
    "focus",
    e => {
      const blurredElement = e.relatedTarget as HTMLElement | null;
      const focusedElement = e.target as HTMLElement | null;

      if (focusedElement) {
        for (const { element, isOpen } of modals.values()) {
          if (isOpen()) {
            // Trap focus inside the dialog if the user tries to focus something outside
            if (element.contains(focusedElement) === false) {
              if (blurredElement) {
                const isTabbingForwards = Boolean(
                  blurredElement.compareDocumentPosition(focusedElement) &
                  Node.DOCUMENT_POSITION_FOLLOWING
                );

                if (isTabbingForwards) {
                  focusFirstDescendant(element);
                } else {
                  focusLastDescendant(element);
                }
              } else {
                focusFirstDescendant(element);
              }
            }

            // We don't support multiple open modals here, so make sure to break after the first one, or we
            // might end up in an infinite focus loop
            break;
          }
        }
      }
    },
    true
  );
};

export const initializeModal = (modal: HTMLElement): Modal => {
  if (!window.ani_modalsInitialized) {
    window.ani_modalsInitialized = true;

    initializeDocumentEventListeners();
  }

  const dialog = modal.querySelector<HTMLElement>("[data-dialog]");

  const dialogContent = modal.querySelector<HTMLElement>("[data-dialog-content]");

  const closeButtons = modal.querySelectorAll<HTMLElement>("[data-close-button]");

  if (!dialog) {
    throw `Modal with id "${modal.id}" dialog missing`;
  }

  if (!dialogContent) {
    throw `Modal with id "${modal.id}" dialog content missing`;
  }

  const getOpenButtons = () =>
    document.querySelectorAll<HTMLElement>(`[aria-controls="${dialog.id}"]`);

  const isOpen = () => modal.inert === false;

  const close = (e?: Event) => {
    if (isOpen()) {
      getOpenButtons().forEach(button => button.setAttribute("aria-expanded", "false"));

      modal.inert = true;

      getOpenButtons()[0]?.focus();

      const event: CloseModalEvent = new CustomEvent("modal:close", {
        detail: { originalEvent: e }
      });

      modal.dispatchEvent(event);
    }
  };

  const open = () => {
    if (!isOpen()) {
      getOpenButtons().forEach(button => button.setAttribute("aria-expanded", "true"));
      modal.inert = false;

      // Focus content for accessibility, but don't scroll the dialog since that will scroll the
      // heading out of view.
      window.requestAnimationFrame(() => dialogContent.focus({ preventScroll: true }));

      modal.dispatchEvent(new Event("modal:open"));
    }
  };

  getOpenButtons().forEach(button =>
    button.addEventListener("click", _ => {
      if (!isOpen()) {
        open();
      }
    })
  );

  addScrollTrap(modal, isOpen);

  if (modal.dataset.closeable === "true") {
    modal.addEventListener("click", e => {
      if (dialog.contains(e.target as Node) === false) {
        close(e);
      }
    });

    closeButtons.forEach(closeButton => {
      closeButton.addEventListener("click", e => {
        close(e);
      });
    });
  }

  const modalDefinition: Modal = {
    close,
    element: modal,
    isOpen,
    open
  };

  modals.set(modal.id, modalDefinition);

  // Move the modal element to the document body, to avoid z-index issues.
  // Note: appendChild actually moves the element from one place to another.
  document.body.appendChild(modal);

  return modalDefinition;
};

export const getModal = (modalElement: HTMLElement) => getModalById(modalElement.id);

export const getModalById = (modalId: string) => {
  if (!modalId.endsWith("-container")) {
    modalId = `${modalId}-container`;
  }

  return modals.get(modalId);
};

export const removeModal = (modalElement: HTMLElement) => {
  const modalRecord = modals.get(modalElement.id);

  if (modalRecord) {
    modals.delete(modalElement.id);

    if (modalRecord.isOpen()) {
      // Close and remove modal element after a delay
      modalRecord.close();

      // Nullify attributes that can identify the modal element
      delete modalElement.dataset.modal;
      modalElement.id = "";

      setTimeout(() => {
        modalElement.remove();
      }, 500);
    } else {
      modalElement.remove();
    }
  }
};

// Check for duplicate modals after HTMX requests, and remove them.
document.addEventListener("htmx:beforeSwap", e => {
  const event = e as BeforeSwapEvent;

  const parser = new DOMParser();
  const doc = parser.parseFromString(event.detail.serverResponse, "text/html");

  doc.querySelectorAll("[data-modal]").forEach(modal => {
    const duplicateModal = document.getElementById(modal.id);

    if (duplicateModal) {
      removeModal(duplicateModal);
    }
  });
});

/** Finds all auto-initializable modals, and initializes them. */
document.querySelectorAll<HTMLElement>("[data-modal]").forEach(element => {
  if (element.dataset.autoInit !== "false") {
    initializeModal(element);
  }
});
